import React, { useEffect, useRef, useState } from "react";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Preferences } from "@capacitor/preferences";
import api from "../../api/Client";
import { SecureStorage } from "../../storage/SecureStorage";

type StoredUser = {
  id: number;
  fullName: string;
  email: string;
  phone: string;
  sex: string;
  dateOfBirth: string;
  postcode?: number | null;
  postAddress?: string;
  dni?: string | null;
  profilePictureName?: string | null;
};

type Document = {
  document: string; // nombre del archivo en el backend
  document_name: string;
  created_at?: string;
};

type DocumentsResponse = {
  documents: Document[];
};

export default function DocumentPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [user, setUser] = useState<StoredUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fileName, setFileName] = useState("");
  const [fileBytes, setFileBytes] = useState<Blob | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Estados para manejar documentos existentes
  const [documents, setDocuments] = useState<Document[] | null>(null);
  const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
  const [errorDocuments, setErrorDocuments] = useState<string | null>(null);

  // === Cargar usuario como en FavoritesPage ===
  useEffect(() => {
    (async () => {
      try {
        let currentUser: StoredUser | null = await SecureStorage.getUser?.() ?? null;
        if (!currentUser) {
          const { value } = await Preferences.get({ key: "user" });
          if (value) currentUser = JSON.parse(value);
        }
        if (!currentUser) {
          setError("No se pudo cargar el usuario. Inicia sesión de nuevo.");
          return;
        }
        setUser(currentUser);
      } catch (e: any) {
        setError(e?.message || "Error cargando el usuario");
      } finally {
        setPageLoading(false);
      }
    })();
  }, []);

  // === Cargar documentos del usuario ===
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user?.id) return;
      setLoadingDocuments(true);
      setErrorDocuments(null);
      try {
        // Intentar primero la nueva ruta móvil, si falla usar la de customers
        let res;
        try {
          res = await api.get(`/api/mobile/user/documents`);
          const docs = (res.data as DocumentsResponse)?.documents || [];
          setDocuments(docs);
        } catch (mobileError) {
          console.warn('Ruta móvil no disponible, usando ruta de customers:', mobileError);
          // Fallback a la ruta de customers
          res = await api.get(`/api/customers/get/${user.id}`);
          const docs = res.data?.data?.documents || [];
          setDocuments(docs);
        }
      } catch (e: any) {
        setErrorDocuments(e?.message || 'Error al cargar documentos');
      } finally {
        setLoadingDocuments(false);
      }
    };
    fetchDocuments();
  }, [user?.id]);

  // === Acciones ===
  const pickFromFiles = () => fileInputRef.current?.click();

  const onFilesSelected: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setFileBytes(f);
  };

  const fromCamera = async () => {
    try {
      const photo = await Camera.getPhoto({
        source: CameraSource.Camera,
        resultType: CameraResultType.Base64,
        quality: 85,
      });
      if (!photo.base64String) return;
      const blob = b64ToBlob(photo.base64String, "image/jpeg");
      const name = `IMG_${Date.now()}.jpg`;
      setFileName(name);
      setFileBytes(blob);
    } catch (e) {
      // silencio
    }
  };

  const fromGallery = async () => {
    try {
      const photo = await Camera.getPhoto({
        source: CameraSource.Photos,
        resultType: CameraResultType.Base64,
        quality: 90,
      });
      if (!photo.base64String) return;
      const mime = photo.format ? `image/${photo.format}` : "image/jpeg";
      const ext = photo.format ?? "jpg";
      const blob = b64ToBlob(photo.base64String, mime);
      const name = `IMG_${Date.now()}.${ext}`;
      setFileName(name);
      setFileBytes(blob);
    } catch (e) {
      // silencio
    }
  };

  const upload = async () => {
    if (!fileBytes || !fileName) return;
    try {
      setLoading(true);
      const form = new FormData();
      // ⬇️ tu backend espera exactamente la clave "file"
      form.append("file", fileBytes, fileName);

      // mismo patrón que el resto: api client con baseURL/headers
      if (!user?.id) {
        setToast("Usuario no cargado");
        return;
      }

      const resp = await api.post(
        `/api/mobile/user/document`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );


      if (resp.status >= 200 && resp.status < 300) {
        setToast("Archivo subido correctamente");
        // opcional: reset
        setFileBytes(null);
        setFileName("");
        
        // Recargar documentos después de subir exitosamente
        try {
          let res;
          try {
            res = await api.get(`/api/mobile/user/documents`);
            const docs = (res.data as DocumentsResponse)?.documents || [];
            setDocuments(docs);
          } catch (mobileError) {
            // Fallback a la ruta de customers
            res = await api.get(`/api/customers/get/${user.id}`);
            const docs = res.data?.data?.documents || [];
            setDocuments(docs);
          }
        } catch (e) {
          // Silencio si falla la recarga, pero el archivo se subió correctamente
        }
      } else {
        setToast(`Error al subir: ${resp.status} ${String(resp.data ?? "")}`);
      }
    } catch (e: any) {
      setToast(`Error al subir: ${e?.message ?? e}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (filename: string) => {
    if (!user?.id) {
      setToast("Usuario no cargado");
      return;
    }

    if (!confirm(`¿Estás seguro de que quieres eliminar el documento "${filename}"? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      setLoading(true);
      const resp = await api.delete(`/api/mobile/user/document/${filename}`);

      if (resp.status >= 200 && resp.status < 300) {
        setToast("Documento eliminado correctamente");
        
        // Recargar documentos después de eliminar exitosamente
        try {
          let res;
          try {
            res = await api.get(`/api/mobile/user/documents`);
            const docs = (res.data as DocumentsResponse)?.documents || [];
            setDocuments(docs);
          } catch (mobileError) {
            // Fallback a la ruta de customers
            res = await api.get(`/api/customers/get/${user.id}`);
            const docs = res.data?.data?.documents || [];
            setDocuments(docs);
          }
        } catch (e) {
          // Silencio si falla la recarga, pero el documento se eliminó correctamente
        }
      } else {
        setToast(`Error al eliminar: ${resp.status} ${String(resp.data ?? "")}`);
      }
    } catch (e: any) {
      setToast(`Error al eliminar: ${e?.message ?? e}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (filename: string, documentName: string) => {
    try {
      setLoading(true);
      const resp = await api.get(`/api/mobile/user/document/${filename}`, {
        responseType: 'blob'
      });

      if (resp.status >= 200 && resp.status < 300) {
        // Crear un blob URL y descargar el archivo
        const blob = new Blob([resp.data]);
        const url = URL.createObjectURL(blob);
        
        // Crear un enlace temporal para descargar
        const link = document.createElement('a');
        link.href = url;
        link.download = documentName || filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpiar el URL del blob
        URL.revokeObjectURL(url);
        
        setToast("Descarga iniciada");
      } else {
        setToast(`Error al descargar: ${resp.status}`);
      }
    } catch (e: any) {
      setToast(`Error al descargar: ${e?.message ?? e}`);
    } finally {
      setLoading(false);
    }
  };

  // === UI (mismo estilo que FavoritesPage) ===
  return (
    <div style={{ position: "relative", minHeight: "100vh", background: "#fff" }}>
      <div style={{ padding: 16 }}>
        {/* Header rojo */}
        <div
          style={{
            background: "#B71C1C",
            color: "white",
            borderRadius: 8,
            padding: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700 }}>Documento</div>
          {/* Muestra base API si quieres debug */}
          {/* <div style={{ opacity: 0.9, fontSize: 13 }}>{API_BASE}</div> */}
        </div>

        {pageLoading && <p style={{ marginTop: 12 }}>Cargando…</p>}
        {error && !pageLoading && <p style={{ color: "red", marginTop: 12 }}>{error}</p>}

        {!pageLoading && !error && (
          <>
            {/* Caja de acciones, estilo tarjeta gris como en Favorites */}
            <div
              style={{
                marginTop: 12,
                background: "#f7f7f7",
                borderRadius: 12,
                padding: 16,
                border: "1px solid transparent",
                display: "grid",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={fromCamera}
                  style={btnPrimary}
                  disabled={loading}
                >
                  Tomar foto
                </button>
                <button
                  onClick={fromGallery}
                  style={btnSecondary}
                  disabled={loading}
                >
                  Galería
                </button>
                <button
                  onClick={pickFromFiles}
                  style={btnSecondary}
                  disabled={loading}
                >
                  Archivos
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={onFilesSelected}
                  // accept="image/*,application/pdf"
                />
              </div>

              {fileName ? (
                <div
                  style={{
                    marginTop: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div style={{ color: "#111827" }}>
                    Archivo: <strong>{fileName}</strong>
                  </div>
                  <button
                    onClick={upload}
                    disabled={!fileBytes || loading}
                    style={{
                      ...btnConfirm,
                      opacity: !fileBytes || loading ? 0.7 : 1,
                      cursor: !fileBytes || loading ? "not-allowed" : "pointer",
                    }}
                  >
                    {loading ? "Subiendo..." : "Subir"}
                  </button>
                </div>
              ) : (
                <p style={{ color: "#6b7280", marginTop: 6 }}>
                  Selecciona un archivo para subir.
                </p>
              )}
            </div>

            {/* Hint inferior suave */}
            <p style={{ marginTop: 16, color: "#777", fontSize: 12 }}>
              Formatos recomendados: imágenes o PDF. Tamaño razonable para evitar timeouts.
            </p>

            {/* === Sección de documentos existentes === */}
            <div
              style={{
                marginTop: 24,
                background: "#f7f7f7",
                borderRadius: 12,
                padding: 16,
                border: "1px solid transparent",
              }}
            >
              <h3 style={{ margin: "0 0 12px 0", fontSize: 16, fontWeight: 600 }}>
                Documentos existentes
              </h3>
              
              {loadingDocuments ? (
                <p style={{ color: "#6b7280", fontSize: 14 }}>Cargando documentos…</p>
              ) : errorDocuments ? (
                <p style={{ color: "#dc2626", fontSize: 14 }}>{errorDocuments}</p>
              ) : documents && documents.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {documents.map((doc) => (
                    <div 
                      key={doc.document} 
                      style={{
                        background: '#fff',
                        borderRadius: 8,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        padding: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 14 }}>
                          {doc.document_name}
                        </div>
                        {doc.created_at && (
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {new Date(doc.created_at).toLocaleDateString('es-ES', { 
                              day: '2-digit', 
                              month: 'short', 
                              year: 'numeric' 
                            })}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => downloadDocument(doc.document, doc.document_name)}
                          disabled={loading}
                          style={{
                            ...btnSecondary,
                            fontSize: 12,
                            padding: '6px 12px',
                            opacity: loading ? 0.7 : 1,
                            cursor: loading ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Descargar
                        </button>
                        <button
                          onClick={() => deleteDocument(doc.document)}
                          disabled={loading}
                          style={{
                            ...btnDanger,
                            fontSize: 12,
                            padding: '6px 12px',
                            opacity: loading ? 0.7 : 1,
                            cursor: loading ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: "#6b7280", fontSize: 14 }}>
                  No hay documentos disponibles.
                </p>
              )}
            </div>
          </>
        )}

        {toast && (
          <div
            style={{
              marginTop: 16,
              color: "#111827",
              background: "#E0F7E9",
              border: "1px solid #10B981",
              borderRadius: 8,
              padding: "8px 12px",
            }}
          >
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}

/* === estilos inline coherentes con FavoritesPage === */
const btnBase: React.CSSProperties = {
  border: "none",
  borderRadius: 8,
  padding: "10px 14px",
  fontWeight: 600,
};

const btnPrimary: React.CSSProperties = {
  ...btnBase,
  background: "#B71C1C",
  color: "white",
};

const btnSecondary: React.CSSProperties = {
  ...btnBase,
  background: "#e5e5e5",
  color: "#111827",
};

const btnConfirm: React.CSSProperties = {
  ...btnBase,
  background: "#10B981",
  color: "white",
};

const btnDanger: React.CSSProperties = {
  ...btnBase,
  background: "#DC2626",
  color: "white",
};

/* Utils */
function b64ToBlob(b64: string, mime = "application/octet-stream") {
  const byteChars = atob(b64);
  const len = byteChars.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = byteChars.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
